import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './DevDashboard.css';

// ============================================
// Status badge component
// ============================================
const StatusBadge = ({ status, isTest }) => {
    const statusStyles = {
        active: 'bg-emerald-500/15 text-emerald-400',
        past_due: 'bg-orange-500/15 text-orange-400',
        suspended: 'bg-red-500/15 text-red-400',
        cancelled: 'bg-slate-500/15 text-slate-400',
        deleted: 'bg-slate-500/15 text-slate-400',
    };

    const displayStatus = status === 'past_due' ? 'Past Due' : status?.charAt(0).toUpperCase() + status?.slice(1);

    return (
        <div className="flex items-center gap-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${statusStyles[status] || statusStyles.active}`}>
                {displayStatus || 'Active'}
            </span>
            {isTest && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-500/15 text-purple-400">
                    TEST
                </span>
            )}
        </div>
    );
};

// ============================================
// Sort indicator
// ============================================
const SortIcon = ({ active, direction }) => {
    if (!active) return <span className="ml-1 text-slate-600 text-xs">↕</span>;
    return <span className="ml-1 text-indigo-400 text-xs">{direction === 'asc' ? '↑' : '↓'}</span>;
};

// ============================================
// Test Account Success Toast
// ============================================
const TestAccountToast = ({ data, onClose }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const text = `Company: ${data.company_name}\nEmail: ${data.email}\nPassword: ${data.password}`;
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="fixed top-5 right-5 z-[1100] bg-slate-800 border border-indigo-500/30 rounded-xl p-5 min-w-[360px] shadow-2xl animate-slideIn">
            <div className="text-base font-semibold text-emerald-400 mb-3 flex items-center gap-2">
                <span>✅</span> Test Account Created
            </div>
            <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Company</span>
                    <span className="text-slate-200 font-mono">{data.company_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Email</span>
                    <span className="text-slate-200 font-mono">{data.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Password</span>
                    <span className="text-slate-200 font-mono">{data.password}</span>
                </div>
            </div>
            <div className="flex gap-2 mt-4">
                <button
                    onClick={handleCopy}
                    className="flex-1 px-3 py-2 bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 rounded-lg text-sm font-medium hover:bg-indigo-500/20 transition-all"
                >
                    {copied ? '✓ Copied!' : 'Copy Credentials'}
                </button>
                <button
                    onClick={onClose}
                    className="px-3 py-2 text-slate-400 hover:text-slate-200 text-sm transition-colors"
                >
                    Close
                </button>
            </div>
        </div>
    );
};

// ============================================
// Company Detail Modal
// ============================================
const CompanyDetailModal = ({ company, onClose, onDelete, onRestore }) => {
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');

    if (!company) return null;

    const isDeleted = !!company.deleted_at;
    const isTest = company.is_test_account;
    const daysUntilPermanent = isDeleted
        ? Math.max(0, 7 - Math.floor((Date.now() - new Date(company.deleted_at).getTime()) / (1000 * 60 * 60 * 24)))
        : 0;

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fadeIn" onClick={onClose} onKeyDown={handleKeyDown}>
            <div className="bg-slate-800 rounded-2xl w-[90%] max-w-[700px] max-h-[85vh] overflow-y-auto border border-slate-700/50 shadow-2xl animate-slideUp" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-7 py-5 border-b border-slate-700/50">
                    <div className="flex items-center gap-3">
                        <h2 className="text-xl font-semibold text-slate-100">{company.company_name}</h2>
                        {isTest && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-500/15 text-purple-400">TEST</span>}
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-200 hover:bg-slate-700/50 p-2 rounded-lg transition-all text-xl leading-none">✕</button>
                </div>

                <div className="px-7 py-6 space-y-7">
                    {/* Restore banner */}
                    {isDeleted && !isTest && (
                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-amber-300 text-sm">
                            <p className="font-medium mb-2">⚠️ This company was deleted on {new Date(company.deleted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                            <p className="text-amber-400/80 mb-3">Data will be permanently removed in <strong>{daysUntilPermanent} day{daysUntilPermanent !== 1 ? 's' : ''}</strong>.</p>
                            <button onClick={() => onRestore(company.id)} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors">
                                Restore Company
                            </button>
                        </div>
                    )}

                    {/* Basic Information */}
                    <div>
                        <h3 className="text-[11px] font-bold uppercase tracking-widest text-indigo-400 mb-4">Basic Information</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <DetailItem label="Company Name" value={company.company_name} />
                            <DetailItem label="Website" value={company.company_website || '—'} />
                            <DetailItem label="Owner" value={company.owner ? `${company.owner.first_name} ${company.owner.last_name}` : '—'} />
                            <DetailItem label="Contact Email" value={company.owner?.email || '—'} />
                            <DetailItem label="Phone" value={company.owner?.phone || company.company_phone || '—'} />
                            <DetailItem label="Account Type" value={isTest ? 'Test Account' : 'Real Customer'} />
                            <DetailItem label="Status" value={<StatusBadge status={isDeleted ? 'deleted' : company.subscription_status} />} />
                            <DetailItem label="Created" value={new Date(company.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} />
                        </div>
                    </div>

                    {/* Subscription */}
                    {!isTest && (
                        <div>
                            <h3 className="text-[11px] font-bold uppercase tracking-widest text-indigo-400 mb-4">Subscription</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <DetailItem label="Monthly Price" value={`£${parseFloat(company.monthly_price || 0).toLocaleString()}/month`} />
                                <DetailItem label="Billing Cycle" value={company.billing_cycle === 'annual' ? 'Annual' : 'Monthly'} />
                                {company.billing_cycle === 'annual' && (
                                    <DetailItem label="Annual Price" value={`£${(parseFloat(company.monthly_price || 0) * 12 * 0.85).toLocaleString()}/year`} />
                                )}
                                <DetailItem label="Max Active Admins" value={company.max_active_admins || 'Unlimited'} />
                                <DetailItem label="Max Onboarders/Month" value={company.max_onboarders_month || 'Unlimited'} />
                                {company.stripe_customer_id && (
                                    <DetailItem label="Stripe Customer" value={
                                        <a href={`https://dashboard.stripe.com/customers/${company.stripe_customer_id}`} target="_blank" rel="noreferrer" className="text-indigo-400 hover:underline text-xs font-mono">
                                            {company.stripe_customer_id}
                                        </a>
                                    } />
                                )}
                            </div>
                        </div>
                    )}

                    {isTest && (
                        <div>
                            <h3 className="text-[11px] font-bold uppercase tracking-widest text-indigo-400 mb-4">Subscription</h3>
                            <p className="text-sm text-slate-500 italic">No subscription (test account)</p>
                        </div>
                    )}

                    {/* Usage */}
                    <div>
                        <h3 className="text-[11px] font-bold uppercase tracking-widest text-indigo-400 mb-4">Usage</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <DetailItem label="Total Users" value={company.usage?.total_users ?? '—'} />
                            <DetailItem label="Active Admins" value={company.usage?.active_admins ?? '—'} />
                            <DetailItem label="Onboarded This Month" value={company.usage?.onboarded_this_month ?? '—'} />
                            <DetailItem label="Onboarded Total" value={company.usage?.onboarded_total ?? '—'} />
                            <DetailItem label="Last Activity" value={
                                company.usage?.last_activity
                                    ? formatRelativeTime(company.usage.last_activity)
                                    : 'No activity'
                            } />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-7 py-5 border-t border-slate-700/50 flex justify-between items-center">
                    {!isDeleted && (
                        <button
                            onClick={() => setDeleteConfirm(true)}
                            className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-sm font-medium hover:bg-red-500/20 transition-all"
                        >
                            {isTest ? 'Delete Test Account' : 'Soft Delete Company'}
                        </button>
                    )}
                    <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-slate-200 text-sm ml-auto transition-colors">
                        Close
                    </button>
                </div>

                {/* Delete Confirmation Overlay */}
                {deleteConfirm && (
                    <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center rounded-2xl">
                        <div className="bg-slate-800 border border-slate-700/50 rounded-xl p-6 max-w-md w-full mx-4">
                            <h3 className="text-lg font-semibold text-slate-100 mb-3">Delete {company.company_name}?</h3>
                            <div className="bg-red-500/8 border border-red-500/20 rounded-lg p-4 mb-4">
                                {isTest ? (
                                    <ul className="list-disc pl-5 space-y-1 text-sm text-slate-400">
                                        <li>This will permanently delete this test account immediately</li>
                                        <li>All data will be removed and cannot be recovered</li>
                                    </ul>
                                ) : (
                                    <ul className="list-disc pl-5 space-y-1 text-sm text-slate-400">
                                        <li>This will revoke access immediately</li>
                                        <li>Stripe subscription will be cancelled</li>
                                        <li>Company data will be kept for 7 days</li>
                                        <li>Company can be restored within 7 days</li>
                                        <li>After 7 days, all data will be permanently deleted</li>
                                    </ul>
                                )}
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm text-slate-400 mb-2">Reason (required)</label>
                                <select
                                    value={deleteReason}
                                    onChange={e => setDeleteReason(e.target.value)}
                                    className="w-full px-3 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 text-sm outline-none focus:border-indigo-500/50"
                                >
                                    <option value="">Select a reason...</option>
                                    {isTest ? (
                                        <>
                                            <option value="testing">Testing/Development</option>
                                            <option value="other">Other</option>
                                        </>
                                    ) : (
                                        <>
                                            <option value="customer_request">Customer Request</option>
                                            <option value="non_payment">Non-Payment</option>
                                            <option value="other">Other</option>
                                        </>
                                    )}
                                </select>
                            </div>
                            <div className="flex gap-3 justify-end">
                                <button onClick={() => setDeleteConfirm(false)} className="px-4 py-2 text-slate-400 hover:text-slate-200 text-sm rounded-lg transition-colors">
                                    Cancel
                                </button>
                                <button
                                    onClick={() => { onDelete(company.id, deleteReason); setDeleteConfirm(false); }}
                                    disabled={!deleteReason}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-all"
                                >
                                    Delete Company
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const DetailItem = ({ label, value }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-xs text-slate-500 font-medium">{label}</span>
        <span className="text-sm text-slate-200">{typeof value === 'string' || typeof value === 'number' ? value : value}</span>
    </div>
);

const formatRelativeTime = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min${mins !== 1 ? 's' : ''} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ============================================
// Create Activation Link Modal
// ============================================
const CreateActivationLinkModal = ({ onClose, onCreated }) => {
    const [formData, setFormData] = useState({
        companyName: '',
        companyWebsite: '',
        companyEmail: '',
        contactName: '',
        contactEmail: '',
        phone: '',
        monthlyPrice: '',
        billingCycle: 'monthly',
        maxActiveAdmins: '',
        maxOnboardersMonth: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [successData, setSuccessData] = useState(null);
    const [copied, setCopied] = useState(false);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const annualPrice = formData.monthlyPrice
        ? (parseFloat(formData.monthlyPrice) * 12).toFixed(2)
        : null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            const res = await api.post('/dev/activation-links', formData);
            setSuccessData(res.data.activationLink);
            onCreated();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create activation link');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(successData.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const inputClass = "w-full px-3 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 text-sm outline-none focus:border-indigo-500/50 transition-colors placeholder:text-slate-600";
    const labelClass = "block text-xs text-slate-400 font-medium mb-1.5";

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fadeIn" onClick={onClose}>
            <div className="bg-slate-800 rounded-2xl w-[90%] max-w-[600px] max-h-[85vh] overflow-y-auto border border-slate-700/50 shadow-2xl animate-slideUp" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-7 py-5 border-b border-slate-700/50">
                    <h2 className="text-xl font-semibold text-slate-100">{successData ? '✅ Link Created' : '+ Add Customer'}</h2>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-200 hover:bg-slate-700/50 p-2 rounded-lg transition-all text-xl leading-none">✕</button>
                </div>

                {successData ? (
                    <div className="px-7 py-6 text-center">
                        <p className="text-slate-300 mb-2 text-sm">Activation link for <strong>{successData.companyName}</strong></p>
                        <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-4">
                            <code className="text-indigo-300 text-sm break-all">{successData.url}</code>
                        </div>
                        <p className="text-xs text-slate-500 mb-4">
                            Expires: {new Date(successData.expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={handleCopyUrl}
                                className="px-5 py-2.5 bg-indigo-500 text-white rounded-lg text-sm font-semibold hover:bg-indigo-600 transition-all"
                            >
                                {copied ? '✓ Copied!' : 'Copy Link'}
                            </button>
                            <button onClick={onClose} className="px-5 py-2.5 text-slate-400 hover:text-slate-200 text-sm transition-colors">Close</button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="px-7 py-6 space-y-5">
                        {/* Company Info */}
                        <div>
                            <h3 className="text-[11px] font-bold uppercase tracking-widest text-indigo-400 mb-3">Company Information</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Company Name <span className="text-red-400">*</span></label>
                                    <input type="text" value={formData.companyName} onChange={e => handleChange('companyName', e.target.value)} className={inputClass} required placeholder="Acme Ltd" />
                                </div>
                                <div>
                                    <label className={labelClass}>Company Email <span className="text-red-400">*</span></label>
                                    <input type="email" value={formData.companyEmail} onChange={e => handleChange('companyEmail', e.target.value)} className={inputClass} required placeholder="info@acme.com" />
                                </div>
                                <div>
                                    <label className={labelClass}>Company Website</label>
                                    <input type="url" value={formData.companyWebsite} onChange={e => handleChange('companyWebsite', e.target.value)} className={inputClass} placeholder="https://acme.com" />
                                </div>
                                <div>
                                    <label className={labelClass}>Phone</label>
                                    <input type="tel" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} className={inputClass} placeholder="+44 ..." />
                                </div>
                            </div>
                        </div>

                        {/* Contact */}
                        <div>
                            <h3 className="text-[11px] font-bold uppercase tracking-widest text-indigo-400 mb-3">Contact Person</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Contact Name <span className="text-red-400">*</span></label>
                                    <input type="text" value={formData.contactName} onChange={e => handleChange('contactName', e.target.value)} className={inputClass} required placeholder="John Smith" />
                                </div>
                                <div>
                                    <label className={labelClass}>Contact Email <span className="text-red-400">*</span></label>
                                    <input type="email" value={formData.contactEmail} onChange={e => handleChange('contactEmail', e.target.value)} className={inputClass} required placeholder="john@acme.com" />
                                </div>
                            </div>
                        </div>

                        {/* Pricing & Limits */}
                        <div>
                            <h3 className="text-[11px] font-bold uppercase tracking-widest text-indigo-400 mb-3">Pricing & Limits</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Monthly Price (£) <span className="text-red-400">*</span></label>
                                    <input type="number" step="0.01" min="0.01" value={formData.monthlyPrice} onChange={e => handleChange('monthlyPrice', e.target.value)} className={inputClass} required placeholder="99.00" />
                                </div>
                                <div>
                                    <label className={labelClass}>Billing Cycle <span className="text-red-400">*</span></label>
                                    <select value={formData.billingCycle} onChange={e => handleChange('billingCycle', e.target.value)} className={inputClass}>
                                        <option value="monthly">Monthly</option>
                                        <option value="annual">Annual</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Max Active Admins <span className="text-red-400">*</span></label>
                                    <input type="number" min="1" value={formData.maxActiveAdmins} onChange={e => handleChange('maxActiveAdmins', e.target.value)} className={inputClass} required placeholder="5" />
                                </div>
                                <div>
                                    <label className={labelClass}>Max Onboarders / Month <span className="text-red-400">*</span></label>
                                    <input type="number" min="1" value={formData.maxOnboardersMonth} onChange={e => handleChange('maxOnboardersMonth', e.target.value)} className={inputClass} required placeholder="50" />
                                </div>
                            </div>
                            {formData.billingCycle === 'annual' && annualPrice && (
                                <div className="mt-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3 text-sm">
                                    <span className="text-slate-400">Annual price: </span>
                                    <span className="text-indigo-300 font-semibold">£{parseFloat(annualPrice).toLocaleString()}/year</span>
                                    <span className="text-slate-500 ml-1">(£{formData.monthlyPrice}/mo × 12)</span>
                                </div>
                            )}
                        </div>

                        {error && (
                            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-300">
                                {error}
                            </div>
                        )}

                        <div className="flex gap-3 justify-end pt-2">
                            <button type="button" onClick={onClose} className="px-4 py-2.5 text-slate-400 hover:text-slate-200 text-sm transition-colors">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="px-5 py-2.5 bg-indigo-500 text-white rounded-lg text-sm font-semibold hover:bg-indigo-600 transition-all shadow-lg shadow-indigo-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitting ? 'Creating...' : 'Create Activation Link'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

// ============================================
// Pending Activation Links Section
// ============================================
const ActivationLinksSection = ({ links }) => {
    const [expanded, setExpanded] = useState(false);
    const [copiedId, setCopiedId] = useState(null);

    const handleCopyLink = (token) => {
        const url = `${window.location.origin}/activate/${token}`;
        navigator.clipboard.writeText(url);
        setCopiedId(token);
        setTimeout(() => setCopiedId(null), 2000);
    };

    return (
        <div className="mt-10">
            <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-2 py-3 text-slate-400 hover:text-slate-200 text-base font-semibold transition-colors select-none"
            >
                <span className={`text-xs transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}>▶</span>
                Pending Activation Links
                <span className="bg-indigo-500/15 text-indigo-300 px-2 py-0.5 rounded-full text-xs font-semibold">
                    {links.length}
                </span>
            </button>

            {expanded && (
                <div className="bg-slate-800 rounded-xl border border-slate-700/50 overflow-hidden">
                    {links.length === 0 ? (
                        <div className="py-12 text-center text-slate-500 text-sm">No pending activation links</div>
                    ) : (
                        <table className="w-full">
                            <thead>
                                <tr className="bg-slate-900/50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Price</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Limits</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Expires</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {links.map(link => (
                                    <tr key={link.id} className="border-t border-slate-700/30 hover:bg-indigo-500/5 transition-colors">
                                        <td className="px-4 py-3 text-sm text-slate-200">{link.company_name}</td>
                                        <td className="px-4 py-3 text-sm text-slate-400">{link.contact_email}</td>
                                        <td className="px-4 py-3 text-sm text-slate-300 text-right font-mono">
                                            £{parseFloat(link.monthly_price).toLocaleString()}/mo
                                            {link.billing_cycle === 'annual' && <span className="text-slate-500 ml-1">(Annual)</span>}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-400">
                                            {link.max_active_admins || '∞'} admins / {link.max_onboarders_month || '∞'} onboarders
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-400">
                                            {new Date(link.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => handleCopyLink(link.token)}
                                                className="px-3 py-1.5 bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 rounded-lg text-xs font-medium hover:bg-indigo-500/20 transition-all"
                                            >
                                                {copiedId === link.token ? '✓ Copied!' : 'Copy Link'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================
// Main DevDashboard Component
// ============================================
const DevDashboard = () => {
    const { logout } = useAuth();

    // Companies state
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });

    // Filters
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showTest, setShowTest] = useState(true);

    // Sorting
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');

    // Modal / toast state
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [testAccountToast, setTestAccountToast] = useState(null);
    const [creatingTest, setCreatingTest] = useState(false);
    const [showCreateLinkModal, setShowCreateLinkModal] = useState(false);

    // Activation links
    const [activationLinks, setActivationLinks] = useState([]);

    // Fetch companies
    const fetchCompanies = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const res = await api.get('/dev/companies', {
                params: {
                    search,
                    status: statusFilter,
                    showTest: showTest.toString(),
                    sortBy,
                    sortOrder,
                    page,
                    limit: 20
                }
            });
            setCompanies(res.data.companies);
            setPagination(res.data.pagination);
        } catch (err) {
            console.error('Failed to fetch companies:', err);
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, showTest, sortBy, sortOrder]);

    // Fetch activation links
    const fetchActivationLinks = useCallback(async () => {
        try {
            const res = await api.get('/dev/activation-links');
            setActivationLinks(res.data.links);
        } catch (err) {
            console.error('Failed to fetch activation links:', err);
        }
    }, []);

    // Initial load + refetch on filter change
    useEffect(() => {
        const debounce = setTimeout(() => {
            fetchCompanies(1);
        }, search ? 300 : 0);
        return () => clearTimeout(debounce);
    }, [fetchCompanies, search]);

    useEffect(() => {
        fetchActivationLinks();
    }, [fetchActivationLinks]);

    // Sort handler
    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    };

    // View company detail
    const handleViewCompany = async (id) => {
        setDetailLoading(true);
        try {
            const res = await api.get(`/dev/companies/${id}`);
            setSelectedCompany(res.data.company);
        } catch (err) {
            console.error('Failed to fetch company detail:', err);
        } finally {
            setDetailLoading(false);
        }
    };

    // Create test account
    const handleCreateTestAccount = async () => {
        setCreatingTest(true);
        try {
            const res = await api.post('/dev/test-account');
            setTestAccountToast(res.data.testAccount);
            fetchCompanies(pagination.page);
        } catch (err) {
            console.error('Failed to create test account:', err);
        } finally {
            setCreatingTest(false);
        }
    };

    // Delete company
    const handleDeleteCompany = async (id, reason) => {
        try {
            await api.delete(`/dev/companies/${id}`, { data: { reason } });
            setSelectedCompany(null);
            fetchCompanies(pagination.page);
        } catch (err) {
            console.error('Failed to delete company:', err);
        }
    };

    // Restore company
    const handleRestoreCompany = async (id) => {
        try {
            await api.post(`/dev/companies/${id}/restore`);
            // Refresh detail
            const res = await api.get(`/dev/companies/${id}`);
            setSelectedCompany(res.data.company);
            fetchCompanies(pagination.page);
        } catch (err) {
            console.error('Failed to restore company:', err);
        }
    };

    // Page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const total = pagination.totalPages;
        const current = pagination.page;

        if (total <= 7) {
            for (let i = 1; i <= total; i++) pages.push(i);
        } else {
            pages.push(1);
            if (current > 3) pages.push('...');
            for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
                pages.push(i);
            }
            if (current < total - 2) pages.push('...');
            pages.push(total);
        }
        return pages;
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
            <div className="max-w-[1440px] mx-auto px-6 sm:px-10 py-8">

                {/* Header */}
                <header className="flex items-start justify-between mb-8 pb-6 border-b border-slate-800">
                    <div>
                        <h1 className="text-[28px] font-bold text-slate-100 tracking-tight">Developer Dashboard</h1>
                        <p className="text-sm text-slate-500 mt-1">Internal platform management</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowCreateLinkModal(true)}
                            className="px-5 py-2.5 bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 rounded-lg text-sm font-semibold hover:bg-indigo-500/20 transition-all"
                        >
                            + Add Customer
                        </button>
                        <button
                            onClick={handleCreateTestAccount}
                            disabled={creatingTest}
                            className="px-5 py-2.5 bg-indigo-500 text-white rounded-lg text-sm font-semibold hover:bg-indigo-600 transition-all shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
                        >
                            {creatingTest ? 'Creating...' : '+ Create Test Account'}
                        </button>
                        <button
                            onClick={logout}
                            className="ml-2 px-3 py-2.5 text-slate-500 hover:text-slate-300 text-sm transition-colors"
                            title="Sign out"
                        >
                            Sign Out
                        </button>
                    </div>
                </header>

                {/* Filters */}
                <div className="flex items-center gap-4 mb-5 flex-wrap">
                    <input
                        type="text"
                        placeholder="Search by company name or email..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="flex-1 min-w-[280px] px-4 py-2.5 bg-slate-800 border border-slate-700/50 rounded-lg text-slate-200 text-sm outline-none focus:border-indigo-500/50 placeholder:text-slate-600 transition-colors"
                    />
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        className="px-4 py-2.5 bg-slate-800 border border-slate-700/50 rounded-lg text-slate-200 text-sm outline-none focus:border-indigo-500/50 cursor-pointer transition-colors"
                    >
                        <option value="all">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="past_due">Past Due</option>
                        <option value="suspended">Suspended</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="deleted">Deleted</option>
                    </select>
                    <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer whitespace-nowrap select-none">
                        <input
                            type="checkbox"
                            checked={showTest}
                            onChange={e => setShowTest(e.target.checked)}
                            className="w-4 h-4 accent-indigo-500 cursor-pointer rounded"
                        />
                        Show test accounts
                    </label>
                </div>

                {/* Companies Table */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-slate-900/60">
                                {[
                                    { key: 'company_name', label: 'Company', align: 'left' },
                                    { key: 'status', label: 'Status', align: 'left' },
                                    { key: 'price', label: 'Price', align: 'right' },
                                    { key: 'created_at', label: 'Created', align: 'left' },
                                    { key: null, label: 'Actions', align: 'right' },
                                ].map(col => (
                                    <th
                                        key={col.label}
                                        onClick={() => col.key && handleSort(col.key)}
                                        className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider border-b border-slate-700/50 transition-colors select-none
                                            ${col.key ? 'cursor-pointer hover:text-slate-200' : ''}
                                            ${sortBy === col.key ? 'text-indigo-400' : 'text-slate-500'}
                                            ${col.align === 'right' ? 'text-right' : 'text-left'}
                                        `}
                                    >
                                        {col.label}
                                        {col.key && <SortIcon active={sortBy === col.key} direction={sortOrder} />}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="py-20 text-center">
                                        <div className="inline-flex items-center gap-3 text-slate-500 text-sm">
                                            <div className="w-5 h-5 border-2 border-slate-600 border-t-indigo-500 rounded-full animate-spin" />
                                            Loading companies...
                                        </div>
                                    </td>
                                </tr>
                            ) : companies.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-16 text-center">
                                        <div className="text-3xl mb-3 opacity-40">
                                            {search ? '🔍' : '🏢'}
                                        </div>
                                        <p className="text-slate-400 font-medium text-sm">
                                            {search
                                                ? `No companies found matching "${search}"`
                                                : "No companies yet. Create your first customer!"}
                                        </p>
                                    </td>
                                </tr>
                            ) : (
                                companies.map(company => (
                                    <tr key={company.id} className="border-t border-slate-700/20 hover:bg-indigo-500/[0.03] transition-colors">
                                        <td className="px-4 py-3.5">
                                            <button
                                                onClick={() => handleViewCompany(company.id)}
                                                className="text-sm font-medium text-slate-200 hover:text-indigo-300 transition-colors text-left"
                                            >
                                                {company.company_name}
                                            </button>
                                            {company.is_test_account && (
                                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-500/15 text-purple-400">
                                                    TEST
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3.5">
                                            <StatusBadge status={company.deleted_at ? 'deleted' : company.subscription_status} />
                                        </td>
                                        <td className="px-4 py-3.5 text-right text-sm tabular-nums text-slate-300 font-mono">
                                            £{parseFloat(company.monthly_price || 0).toLocaleString()}/mo
                                            {company.billing_cycle === 'annual' && <span className="text-slate-500 text-xs ml-1">(Annual)</span>}
                                        </td>
                                        <td className="px-4 py-3.5 text-sm text-slate-400">
                                            {new Date(company.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </td>
                                        <td className="px-4 py-3.5 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    onClick={() => handleViewCompany(company.id)}
                                                    className="p-2 text-slate-500 hover:text-indigo-400 hover:bg-slate-700/50 rounded-lg transition-all text-xs"
                                                    title="View details"
                                                >
                                                    👁
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        handleViewCompany(company.id);
                                                    }}
                                                    className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all text-xs"
                                                    title="Delete"
                                                >
                                                    🗑
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    {!loading && pagination.totalPages > 1 && (
                        <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-700/50">
                            <span className="text-xs text-slate-600">
                                Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                            </span>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => fetchCompanies(pagination.page - 1)}
                                    disabled={pagination.page <= 1}
                                    className="px-3 py-1.5 border border-slate-700/50 rounded-md text-xs text-slate-400 hover:bg-indigo-500/10 hover:border-indigo-500/30 hover:text-indigo-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                >
                                    Previous
                                </button>
                                {getPageNumbers().map((p, i) => (
                                    p === '...' ? (
                                        <span key={`dots-${i}`} className="px-2 py-1.5 text-xs text-slate-600">…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            onClick={() => fetchCompanies(p)}
                                            className={`px-3 py-1.5 rounded-md text-xs transition-all ${p === pagination.page
                                                ? 'bg-indigo-500 text-white border border-indigo-500'
                                                : 'border border-slate-700/50 text-slate-400 hover:bg-indigo-500/10 hover:border-indigo-500/30 hover:text-indigo-300'
                                                }`}
                                        >
                                            {p}
                                        </button>
                                    )
                                ))}
                                <button
                                    onClick={() => fetchCompanies(pagination.page + 1)}
                                    disabled={pagination.page >= pagination.totalPages}
                                    className="px-3 py-1.5 border border-slate-700/50 rounded-md text-xs text-slate-400 hover:bg-indigo-500/10 hover:border-indigo-500/30 hover:text-indigo-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Activation Links */}
                <ActivationLinksSection links={activationLinks} />
            </div>

            {/* Modals & Toasts */}
            {selectedCompany && (
                <CompanyDetailModal
                    company={selectedCompany}
                    onClose={() => setSelectedCompany(null)}
                    onDelete={handleDeleteCompany}
                    onRestore={handleRestoreCompany}
                />
            )}
            {testAccountToast && (
                <TestAccountToast
                    data={testAccountToast}
                    onClose={() => setTestAccountToast(null)}
                />
            )}
            {showCreateLinkModal && (
                <CreateActivationLinkModal
                    onClose={() => setShowCreateLinkModal(false)}
                    onCreated={() => fetchActivationLinks()}
                />
            )}
        </div>
    );
};

export default DevDashboard;
